import React from 'react';
import TableRowActionMenu from '../../../BaseComponents/TableRowActionMenu';
import { useDispatch } from 'react-redux';
import {
  changeDeleteBodySystemModalVisibility,
  changeUpdateBodySystemModalVisibility,
  setBodySystemToEdit,
} from '../../../../store/appSlice';
import {
  BodySystemPromptField,
  ListBodySystemResponseElement,
} from '../../../../types/apiGeneratedTypes';
import { applicableModalities } from '../../../../utils/constants';
import './BodySystemsTable.scss';

const modalityLabelByKey = new Map(
  applicableModalities.map((modality) => [modality.key, modality.value])
);

type Props = {
  bodySystemData: ListBodySystemResponseElement;
  onEditPrompt: (
    bodySystem: ListBodySystemResponseElement,
    field: BodySystemPromptField
  ) => void;
  onGenerateScores: (bodySystem: ListBodySystemResponseElement) => void;
};

type TagItem = {
  id: number;
  label: string;
};

const sortTagItems = (items: TagItem[]) =>
  [...items]
    .filter((item) => item.label.trim())
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );

const renderTagList = (items: TagItem[], tagClassName: string) => {
  const sortedItems = sortTagItems(items);

  if (sortedItems.length === 0) {
    return <span className='empty-value'>-</span>;
  }

  return (
    <div className='association-tags'>
      {sortedItems.map((item) => (
        <span key={item.id} className={`association-tag ${tagClassName}`}>
          {item.label.trim()}
        </span>
      ))}
    </div>
  );
};

const BodySystemsTableRow = ({ bodySystemData, onEditPrompt, onGenerateScores }: Props) => {
  const dispatch = useDispatch();

  const biomarkerItems: TagItem[] = (bodySystemData.biomarkers ?? []).map((biomarker) => ({
    id: biomarker.id,
    label: biomarker.name,
  }));
  const mriConditionItems: TagItem[] = (bodySystemData.mri_conditions ?? []).map((condition) => ({
    id: condition.id,
    label: condition.condition_description,
  }));

  const applicableModalityItems: TagItem[] = (bodySystemData.applicable_modalities ?? []).map(
    (modality, index) => ({
      id: index,
      label: modalityLabelByKey.get(modality) ?? modality,
    })
  );

  const promptFields = new Set((bodySystemData.prompts ?? []).map((prompt) => prompt.field));
  const promptItems: TagItem[] = [
    ...(promptFields.has('summary') ? [{ id: 1, label: 'Summary' }] : []),
    ...(promptFields.has('score') ? [{ id: 2, label: 'Score' }] : []),
  ];

  const onOptionClick = (_: string, action: string) => {
    if (action === 'edit') {
      dispatch(setBodySystemToEdit(bodySystemData));
      dispatch(changeUpdateBodySystemModalVisibility(true));
    } else if (action === 'edit_summary_prompt') {
      onEditPrompt(bodySystemData, 'summary');
    } else if (action === 'edit_score_prompt') {
      onEditPrompt(bodySystemData, 'score');
    } else if (action === 'generate_scores') {
      onGenerateScores(bodySystemData);
    } else if (action === 'delete') {
      dispatch(setBodySystemToEdit(bodySystemData));
      dispatch(changeDeleteBodySystemModalVisibility(true));
    }
  };

  return (
    <div className='table-row body-systems-table-row'>
      <div className='icon'>
        {bodySystemData.icon_url ? (
          <img src={bodySystemData.icon_url} alt='body system' />
        ) : (
          '-'
        )}
      </div>
      <div className='body-system'>
        {bodySystemData.title ? bodySystemData.title : '-'}
      </div>
      <div className='description'>
        {bodySystemData.description ? bodySystemData.description : '-'}
      </div>
      <div className='biomarkers'>
        {renderTagList(biomarkerItems, 'biomarker-tag')}
      </div>
      <div className='mri-conditions'>
        {renderTagList(mriConditionItems, 'mri-condition-tag')}
      </div>
      <div className='applicable-modalities'>
        {renderTagList(applicableModalityItems, 'applicable-modality-tag')}
      </div>
      <div className='prompts'>
        {renderTagList(promptItems, 'prompt-tag')}
      </div>
      <div className='actions'>
        <TableRowActionMenu
          options={[
            { key: 'edit', value: 'Edit' },
            { key: 'edit_summary_prompt', value: 'Edit Summary prompt' },
            { key: 'edit_score_prompt', value: 'Edit Score prompt' },
            { key: 'generate_scores', value: 'Generate scores and summary' },
            { key: 'delete', value: 'Delete' },
          ]}
          menuWidth={280}
          id={bodySystemData.id}
          onActionMenuClick={onOptionClick}
        />
      </div>
    </div>
  );
};

export default BodySystemsTableRow;
